import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import type {
  IDataTable,
  TFilters,
  TFiltersParams,
} from '../../types/data_table';

/** The four regions that show a skeleton while the table is fetching. */
export interface IDataTableLoading {
  header: boolean;
  filters: boolean;
  pagination: boolean;
  table: boolean;
}

export interface IUseDataTableQuery {
  lang: IDataTable['lang'];
  /** The consumer's `queries` config. */
  queries: IDataTable['queries'];
  /** Applied filters, merged into every request. */
  params: TFiltersParams;
  /** Filter ids grouped by type, forwarded to `onResponseChange`. */
  filtersTypes?: Record<TFilters, string[]>;
  /** `false` while default filter selections are still being seeded. */
  paramsReady: boolean;
  /** Page sizes offered by the pager; the first one is the initial size. */
  pageSizeOptions?: number[];
  /** Whether the consumer is still fetching the filters themselves. */
  isFiltersLoading: boolean;
  /** Consumer callbacks fired when the user pages. */
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Consumer callback fired whenever the request's error state changes. */
  onError?: (error: boolean) => void;
}

export interface IDataTableQuery {
  currentPage: number;
  pageSize: number;
  /**
   * The raw response body, untyped because `queryFunction` is. `undefined`
   * until the first request resolves.
   */
  response: any;
  isError: boolean;
  loading: IDataTableLoading;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
}

/**
 * Owns paging state and the request itself, and derives the per-region loading
 * flags the container hands to its children.
 *
 * The query is deliberately conservative about when it may run — see
 * `isQueryEnabled` below — because every avoidable fetch here is a visible
 * flash of skeleton in a table that is often the whole page.
 */
export const useDataTableQuery = ({
  lang,
  queries,
  params,
  filtersTypes,
  paramsReady,
  pageSizeOptions,
  isFiltersLoading,
  onPageChange,
  onPageSizeChange,
  onError,
}: IUseDataTableQuery): IDataTableQuery => {
  const {
    enabledQuery,
    queryKeys,
    queryFunction,
    queryFunctionParams,
    resetFiltersParams,
    onResponseChange,
    onLoadingChange,
  } = queries;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions?.[0] || 10);

  /**
   * Fingerprint of everything that should send the user back to page one.
   * `queryKeys` and `resetFiltersParams` are usually inline literals, so this
   * compares by content rather than by identity.
   */
  const currentFilters = JSON.stringify({
    params,
    queryKeys,
    pageSize,
    ...resetFiltersParams,
  });

  /**
   * The fingerprint the current `currentPage` belongs to. Held in state, not a
   * ref, because the query stays disabled until the two agree and it is this
   * update that re-renders and releases it.
   */
  const [settledFilters, setSettledFilters] = useState(currentFilters);

  /**
   * Blocks the one render between a filter changing and `currentPage` being
   * reset, so page 3 of the old filters is never requested with the new ones.
   */
  const areFiltersSettled = currentFilters === settledFilters;

  /** A blank required param means the consumer is not ready to be queried. */
  const hasAllRequiredParams = Object.values(queryFunctionParams || {}).every(
    (value) => value !== undefined && value !== null && value !== ''
  );

  const isQueryEnabled =
    enabledQuery !== undefined
      ? enabledQuery
      : paramsReady && areFiltersSettled && hasAllRequiredParams;

  const {
    isFetching: isQueryLoading,
    isError,
    data: response,
  } = useQuery({
    queryKey: [
      ...queryKeys,
      currentPage,
      pageSize,
      params,
      queryFunctionParams,
      lang,
    ],
    queryFn: () =>
      queryFunction({
        currentPage,
        pageSize,
        params: { ...params, ...queryFunctionParams },
      }),
    enabled: isQueryEnabled,
  });

  // Any change to the filters, page size or query identity puts the user back
  // on page one, and re-arms the guard above for the next change.
  useEffect(() => {
    setCurrentPage(1);
    setSettledFilters(currentFilters);
  }, [params, queryKeys, queryFunctionParams, pageSize, resetFiltersParams]);

  useEffect(() => {
    onError?.(isError);
  }, [isError]);

  // Surface the untouched response so consumers can read fields the table
  // itself does not render (totals, summaries, export links...).
  useEffect(() => {
    if (!response || !onResponseChange) return;

    onResponseChange(response, params, filtersTypes);
  }, [response]);

  useEffect(() => {
    onLoadingChange?.(isQueryLoading);
  }, [isQueryLoading]);

  // A query that is not allowed to run yet is still "loading" as far as the UI
  // is concerned: the data it will show has not arrived.
  const isTableLoading = !isQueryEnabled || isQueryLoading;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onPageChange?.(page);
  };

  const handlePageSizeChange = (size: number) => {
    onPageSizeChange?.(size);
    setPageSize(size);
  };

  return {
    currentPage,
    pageSize,
    response,
    isError,
    loading: {
      header: isTableLoading,
      filters: isFiltersLoading,
      pagination: isTableLoading,
      table: isTableLoading,
    },
    handlePageChange,
    handlePageSizeChange,
  };
};
